import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'; // Added useMemo and useRef
import { useNavigate } from 'react-router-dom';
import { Outlet, useLocation } from 'react-router-dom';
// Import necessary MUI components
import { TextField, IconButton, Autocomplete, Chip, FormControl } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles'; // Added
import rtlPlugin from 'stylis-plugin-rtl'; // Added
import { CacheProvider } from '@emotion/react'; // Added
import createCache from '@emotion/cache'; // Added
import { prefixer } from 'stylis'; // Added
import { CasesContextProvider, useCases } from './CasesContext'; // Import useCases
import Loader from '../../components/Loader'; // Added Loader
import CasesList from './CasesList';
import { casesApi } from './caseApi';
import CommonLayout from '../../components/CommonLayout';
import FloatingAddButton from '../../components/FloatingAddButton';
import { UserContext } from '../../context/UserContext';
import { permissions } from "../../utils/permissions-utils";
import UploadErrors from '../../components/UploadErrors';
import { validateFile, extensionsFrom } from '../../utils/file-validation';
import './MedicalCases.scss';
import './global.scss';

// The mobile "new case" button opens the camera / share sheet straight away, so
// what it offers has to be exactly what the case form and the server accept —
// mirror of the `accept` map in AddEditMedicalCase.jsx, itself a mirror of
// config/multer.js. Anything missing here is refused client-side even though
// the server would have taken it, so keep the three lists in step.
const MOBILE_CAPTURE_ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/rtf': ['.rtf'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'message/rfc822': ['.eml'],
  'application/vnd.ms-outlook': ['.msg'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
  'image/bmp': ['.bmp'],
  'image/tiff': ['.tif', '.tiff'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
};
const MOBILE_CAPTURE_MAX_SIZE = 20 * 1024 * 1024; // matches medicalCasesRoutes.js
const MOBILE_CAPTURE_ACCEPT_ATTR = [
  ...Object.keys(MOBILE_CAPTURE_ACCEPT),
  ...extensionsFrom(MOBILE_CAPTURE_ACCEPT)
].join(',');

// MUI RTL Cache and Theme (similar to Protocols.jsx)
const cacheRtl = createCache({
  key: 'muirtl-cases', // Unique key for this instance
  stylisPlugins: [prefixer, rtlPlugin],
});

const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' },
  direction: 'rtl',
});

// --- Icons ---
const FilterSliderIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 6C11.448 6 11 5.552 11 5C11 4.448 11.448 4 12 4C12.552 4 13 4.448 13 5C13 5.552 12.552 6 12 6ZM15 5C15 3.346 13.654 2 12 2C10.346 2 9 3.346 9 5C9 6.302 9.839 7.401 11 7.815V21C11 21.553 11.447 22 12 22C12.553 22 13 21.553 13 21V7.815C14.161 7.401 15 6.302 15 5ZM6 18C5.448 18 5 17.552 5 17C5 16.448 5.448 16 6 16C6.552 16 7 16.448 7 17C7 17.552 6.552 18 6 18ZM7 14.185V3C7 2.447 6.553 2 6 2C5.447 2 5 2.447 5 3V14.185C3.839 14.599 3 15.698 3 17C3 18.302 3.839 19.401 5 19.815V21C5 21.553 5.447 22 6 22C6.553 22 7 21.553 7 21V19.815C8.161 19.401 9 18.302 9 17C9 15.698 8.161 14.599 7 14.185ZM18 14C17.448 14 17 13.552 17 13C17 12.448 17.448 12 18 12C18.552 12 19 12.448 19 13C19 13.552 18.552 14 18 14ZM21 13C21 11.698 20.161 10.599 19 10.185V3C19 2.447 18.553 2 18 2C17.447 2 17 2.447 17 3V10.185C15.839 10.599 15 11.698 15 13C15 14.302 15.839 15.401 17 15.815V21C17 21.553 17.447 22 18 22C18.553 22 19 21.553 19 21V15.815C20.161 15.401 21 14.302 21 13Z" fill="currentColor"/>
  </svg>
);

// Copied from ProtocolSearchBox.jsx
const ClearIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.2926 14.707C11.4876 14.902 11.7436 15 11.9996 15C12.2556 15 12.5116 14.902 12.7066 14.707L13.9996 13.414L15.2926 14.707C15.4876 14.902 15.7436 15 15.9996 15C16.2556 15 16.5116 14.902 16.7066 14.707C17.0976 14.316 17.0976 13.684 16.7066 13.293L15.4136 12L16.7066 10.707C17.0976 10.316 17.0976 9.684 16.7066 9.293C16.3156 8.902 15.6836 8.902 15.2926 9.293L13.9996 10.586L12.7066 9.293C12.3156 8.902 11.6836 8.902 11.2926 9.293C10.9016 9.684 10.9016 10.316 11.2926 10.707L12.5856 12L11.2926 13.293C10.9016 13.684 10.9016 14.316 11.2926 14.707ZM19.9996 18H10.3696C10.1206 18 9.88158 17.907 9.69658 17.739L4.36858 12.894L9.74558 6.219C9.92258 6.077 10.1426 6 10.3696 6H19.9996V18ZM20.1406 4H10.3696C9.62158 4 8.90458 4.277 8.35158 4.781C8.31258 4.815 8.27758 4.854 8.24558 4.894L2.22158 12.373C1.88958 12.785 1.93558 13.384 2.32658 13.74L8.35158 19.219C8.90458 19.723 9.62158 20 10.3696 20H20.1406C21.1656 20 21.9996 19.185 21.9996 18.182V5.818C21.9996 4.815 21.1656 4 20.1406 4Z" fill="#0C3058"/>
  </svg>
);

const MedicalCases = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter state
  // const [allTags, setAllTags] = useState([]); // Removed: Get from context
  const [selectedTags, setSelectedTags] = useState([]); // Array of { id: string, name: string }
  const [selectedHospital, setSelectedHospital] = useState(null); // Stores hospital object { _id, nameHebrew, ... }
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showHospitalFilter, setShowHospitalFilter] = useState(false);
  const [showDepartmentFilter, setShowDepartmentFilter] = useState(false);


  const { user, isInitialized } = useContext(UserContext); // Get hospitals from context

  // Determine if hospital filter should be shown
  useEffect(() => {
    if (isInitialized && user) {
      const userRoles = user?.roles || [];
      const isSuperAdmin = user?.isSuperAdmin || false;
      const associatedHospitalIds = userRoles.map(role => role.hospital).filter(Boolean);
      const uniqueHospitalIds = [...new Set(associatedHospitalIds)];
      // Show if super admin, or if user is associated with more than one hospital.
      const shouldShow = isSuperAdmin || uniqueHospitalIds.length > 1;
      setShowHospitalFilter(shouldShow);
    } else {
      setShowHospitalFilter(false);
    }
  }, [user, isInitialized]);

  useEffect(() => {
    if (isInitialized && user) {
      const userRoles = user?.roles || [];
      const isSuperAdmin = user?.isSuperAdmin || false;
      const associatedDepartmentIds = userRoles.map(role => role.department).filter(Boolean);
      const uniqueDepartmentIds = [...new Set(associatedDepartmentIds)];
      const shouldShow = isSuperAdmin || uniqueDepartmentIds.length > 1;
      setShowDepartmentFilter(shouldShow);
    } else {
      setShowDepartmentFilter(false);
    }
  }, [user, isInitialized]);


  // Check if we're on the main cases list or a sub-route
  const isMainCasesRoute = location.pathname === '/cases' || location.pathname === '/cases/'; // Corrected check 

  const canCreateMedicalCase = () => {
      return permissions.canCreate(user);
    };
  

  // Pass searchTerm, selectedTags, and selectedHospital ID to the provider
  return (
    <CasesContextProvider
      searchTerm={searchTerm}
      selectedTags={selectedTags}
      selectedHospital={selectedHospital?._id} // Pass only the ID
      selectedDepartment={selectedDepartment?._id}
    >
      <div className="cases-page mc">
        {isMainCasesRoute ? (
          <CommonLayout maxWidth="1000px">
            <CasesPageContent
              searchTerm={searchTerm}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              selectedHospital={selectedHospital}
              setSearchTerm={setSearchTerm}
              setSelectedHospital={setSelectedHospital}
              selectedDepartment={selectedDepartment}
              setSelectedDepartment={setSelectedDepartment}
              showHospitalFilter={showHospitalFilter}
              showDepartmentFilter={showDepartmentFilter}
              canCreateMedicalCase={canCreateMedicalCase}
            />
          </CommonLayout>
        ) : (
          // Wrap Outlet in CommonLayout and page-container for consistent styling
            <CommonLayout>
              <div id="cases" className="page-container">
                <Outlet />
              </div>          
            </CommonLayout>
        )}
      </div>      
    </CasesContextProvider>
  );
};


  // Helper component to consume context *inside* the provider
  const CasesPageContent = ({
    selectedHospital,
    selectedTags,
    searchTerm,
    showHospitalFilter,
    setSelectedTags,
    setSearchTerm,
    setSelectedHospital,
    selectedDepartment,
    setSelectedDepartment,
    showDepartmentFilter,
    canCreateMedicalCase
  }) => {
    const { allTags, isLoading, cases } = useCases(); // Consume context here, Added isLoading and cases
    const navigate = useNavigate(); // Ensure navigate is available in this scope
    const fileInputRef = useRef(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isAnyFilterActive, setIsAnyFilterActive] = useState(false);
    // Rejection from the mobile capture picker, shown above the list.
    const [pickError, setPickError] = useState('');
    const [autocompleteData, setAutocompleteData] = useState({
      hospitals: [],
      departments: [],
      tags: [],
    });

    useEffect(() => {
      const fetchAutocompleteData = async () => {
        try {
          const response = await casesApi.getAutocompleteData();
          setAutocompleteData(response.data);
        } catch (error) {
          console.error('Error fetching autocomplete data:', error);
        }
      };
      fetchAutocompleteData();
    }, []);

    useEffect(() => {
      const isActive = !!searchTerm || selectedTags.length > 0 || !!selectedHospital || !!selectedDepartment;
      setIsAnyFilterActive(isActive);
    }, [searchTerm, selectedTags, selectedHospital, selectedDepartment]);

    const isMobileDevice = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const handleFileSelected = (event) => {
      const file = event.target.files[0];
      // Reset first: the same file has to be re-pickable after a rejection.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (!file) return;

      // This file bypasses the new-case dropzone (it arrives as an initial
      // file), so it is checked here instead — otherwise a 40MB clip is only
      // refused once the whole form has been filled in and submitted.
      const rejection = validateFile(file, {
        accept: MOBILE_CAPTURE_ACCEPT,
        maxSize: MOBILE_CAPTURE_MAX_SIZE
      });
      if (rejection) {
        setPickError(rejection.message);
        return;
      }

      setPickError('');
      navigate('/cases/new', { state: { uploadedFile: file } });
    };

    const handleAddNewCaseClick = () => {
      if (isMobileDevice()) {
        fileInputRef.current?.click();
      } else {
        navigate('/cases/new');
      }
    };

    const handleSearch = (e) => {
      setSearchTerm(e.target.value);
    };
  
    const toggleFilter = () => {
      setIsFilterOpen(!isFilterOpen);
    };
  
    // --- Filter Handlers ---
    const handleHospitalSelect = (event, newValue) => {
      setSelectedHospital(newValue); // newValue is the hospital object or null
      // Optionally close filter panel on select
      // setIsFilterOpen(false);
    };
  
    const handleTagsSelect = (event, newValue) => {
      // newValue is an array of tag objects { id, name }
      setSelectedTags(newValue);
      // Optionally close filter panel on select
      // setIsFilterOpen(false);
    };

    const handleDepartmentSelect = (event, newValue) => {
      setSelectedDepartment(newValue);
    };
  
    const handleDeleteFilter = (filterToDelete) => {
      if (filterToDelete.type === 'hospital') {
        setSelectedHospital(null);
      } else if (filterToDelete.type === 'department') {
        setSelectedDepartment(null);
      } else if (filterToDelete.type === 'tag') {
        setSelectedTags(prev => prev.filter(tag => tag.id !== filterToDelete.id));
      }
    };
  
    const handleClearAllFilters = () => {
      setSelectedHospital(null);
      setSelectedDepartment(null);
      setSelectedTags([]);
      // Optionally close filter panel
      // setIsFilterOpen(false);
    };

      // --- Derived State for Chips ---
  const selectedFiltersForChips = useMemo(() => {
    const filters = [];
    if (selectedHospital) {
      filters.push({
        type: 'hospital',
        id: selectedHospital._id,
        label: selectedHospital.nameHebrew || selectedHospital.nameEnglish || 'בית חולים',
      });
    }
    if (selectedDepartment) {
      filters.push({
        type: 'department',
        id: selectedDepartment._id,
        label: selectedDepartment.nameHebrew || 'מחלקה',
      });
    }
    selectedTags.forEach(tag => {
      filters.push({
        type: 'tag',
        id: tag.id, // Use tag.id which is the tag name string
        label: tag.name,
      });
    });
    return filters;
  }, [selectedHospital, selectedDepartment, selectedTags]);

    return (
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <>
            {/* <Loader isLoading={isLoading} /> */}
            <input
              type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileSelected}
          // Exactly the formats the case form itself takes. `image/*` used to be
          // here and let the iPhone share sheet offer .heic, which the server
          // then refuses — after the user has already filled in the whole form.
          accept={MOBILE_CAPTURE_ACCEPT_ATTR}
        />
        <UploadErrors
          messages={pickError ? [pickError] : []}
          onDismiss={() => setPickError('')}
        />
        <div className="cases-header">
              {/* Replicate search-container structure with prefixed classes */}
              <div className="cases-search-container"> {/* Prefixed */}
                <TextField
                  className="cases-search-input" // Prefixed
                  variant="outlined"
                  placeholder="חיפוש מקרים..."
                  value={searchTerm}
                  onChange={handleSearch}
                  // sx prop removed, styling will come from CSS
                />
                <IconButton
                  className={`cases-filter-button ${isFilterOpen ? 'active' : ''}`} // Prefixed
                  onClick={toggleFilter}
                >
                  <FilterSliderIcon />
                </IconButton>
              </div>
              {/* Filter Panel */}
              <div className={`cases-filter-panel-container ${isFilterOpen ? 'open' : ''}`}>
                <div className={`cases-filter-panel ${isFilterOpen ? 'open' : ''}`}>
                  <div className="filters-grid">
                    {/* Hospital Filter */}
                    {showHospitalFilter && (
                      <FormControl className="filter-field">
                        <label className="filter-label">בית חולים</label>
                        <Autocomplete
                          value={selectedHospital}
                          onChange={handleHospitalSelect}
                          options={autocompleteData.hospitals}
                          noOptionsText="לא נמצאו בתי חולים"
                          getOptionLabel={(option) => option.nameHebrew || option.nameEnglish || ''}
                          isOptionEqualToValue={(option, value) => option._id === value?._id}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="בית חולים" />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={`hospital-${option._id}`}>
                              {option.nameHebrew || option.nameEnglish}
                            </li>
                          )}
                          disableClearable // Remove X icon
                        />
                      </FormControl>
                    )}

                    {showDepartmentFilter && (
                      <FormControl className="filter-field">
                        <label className="filter-label">מחלקה</label>
                        <Autocomplete
                          value={selectedDepartment}
                          onChange={handleDepartmentSelect}
                          options={autocompleteData.departments}
                          noOptionsText="לא נמצאו מחלקות"
                          getOptionLabel={(option) => option.nameHebrew || ''}
                          isOptionEqualToValue={(option, value) => option._id === value?._id}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="מחלקה" />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={`department-${option._id}`}>
                              {option.nameHebrew}
                            </li>
                          )}
                          disableClearable
                        />
                      </FormControl>
                    )}

                    {/* Tags Filter */}
                    <FormControl className="filter-field">
                      <label className="filter-label">תגיות</label>
                      <Autocomplete
                        multiple
                        value={selectedTags} // Expects array of {id, name}
                        onChange={handleTagsSelect}
                        options={allTags || []} // Use allTags from context, provide default empty array
                        noOptionsText="לא נמצאו תגיות"
                        style={{
                          maxWidth: !showHospitalFilter && !showDepartmentFilter ? '350px' : 'none'
                        }}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="בחר תגיות"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={`tag-${option.id}`}>
                            {option.name}
                          </li>
                        )}
                        renderTags={() => null} // Do not render tags inside the input
                        disableClearable // Match ProtocolSearchBox
                        forcePopupIcon={true} // Match ProtocolSearchBox for triangle icon
                        filterSelectedOptions={false} // Match ProtocolSearchBox
                        clearOnBlur={false} // Match ProtocolSearchBox
                      />
                    </FormControl>
                  </div>
                </div>
              </div>
              {/* End Filter Panel */}

              {/* Selected Filters Chips Display */}
              {selectedFiltersForChips.length > 0 && (
                <div className="cases-selected-chips">
                  {selectedFiltersForChips.map((filter) => (
                    <Chip
                      key={`${filter.type}-${filter.id}`}
                      label={filter.label}
                      onDelete={() => handleDeleteFilter(filter)}
                      className="chip" // Use generic chip class or create cases-chip
                      size="small"
                    />
                  ))}
                  <button
                    className="clear-filters-button" // Match ProtocolSearchBox
                    onClick={handleClearAllFilters}
                  >
                    <ClearIcon /> {/* Use the SVG icon */}
                    נקה סינון
                  </button>
                </div>
              )}
              {/* End Selected Filters Chips Display */}
            </div>            
              <div id="cases" className="page-container">
                {/* CasesList now gets data from context, no need to pass props here */}
                <CasesList
                 handleAddNewCaseClick={handleAddNewCaseClick}
                 isAnyFilterActive={isAnyFilterActive}
               />

               {canCreateMedicalCase() && ( 
                 <FloatingAddButton
                   onClick={handleAddNewCaseClick}
                   entityName="מקרה"
                   dataLoaded={!isLoading}
                 />
               )}
            </div>
          </>
        </ThemeProvider>
      </CacheProvider>
    );
  };

export default MedicalCases;
